import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Search, FileText, MessageSquare, Building2, Star, Loader2, MapPin, Calendar, ArrowRight, AlertCircle, CreditCard, Award, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StarDisplay({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star key={i} className={`w-4 h-4 ${i <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`} />
    );
  }
  return <div className="flex gap-0.5">{stars}</div>;
}

function ReviewCard({ review, onReply }) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submitReply() {
    if (!replyText.trim()) { toast.error('Wpisz odpowiedź'); return; }
    setSubmitting(true);
    const success = await onReply(review.review_id, replyText);
    if (success) {
      setShowReply(false);
      setReplyText('');
    }
    setSubmitting(false);
  }

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span className="font-medium">{review.user_name}</span>
          <StarDisplay rating={review.rating} />
        </div>
        <span className="text-xs text-slate-500">{new Date(review.created_at).toLocaleDateString('pl-PL')}</span>
      </div>
      <p className="text-sm text-slate-600 mb-3">{review.comment}</p>
      
      {review.company_reply ? (
        <div className="ml-4 p-3 bg-orange-50 rounded-lg border-l-2 border-orange-500">
          <p className="text-xs text-orange-600 font-medium mb-1">Twoja odpowiedź:</p>
          <p className="text-sm">{review.company_reply}</p>
        </div>
      ) : (
        showReply ? (
          <div className="mt-3 space-y-2">
            <Textarea 
              value={replyText} 
              onChange={(e) => setReplyText(e.target.value)} 
              rows={3} 
              placeholder="Napisz odpowiedź na opinię..."
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowReply(false)}>Anuluj</Button>
              <Button size="sm" onClick={submitReply} disabled={submitting} className="bg-orange-500 hover:bg-orange-600">
                {submitting ? 'Wysyłanie...' : 'Odpowiedz'}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setShowReply(true)} className="text-orange-600 border-orange-200">
            <MessageCircle className="w-4 h-4 mr-1" />Odpowiedz na opinię
          </Button>
        )
      )}
    </div>
  );
}

export default function CompanyDashboard() {
  const { user, token, isAuthenticated } = useAuth();
  const { isFreeMode, showPaymentUI } = useFeatureFlags();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [myResponses, setMyResponses] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [recentOffers, setRecentOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('offers');

  const isCompany = user?.user_type === 'company';
  const hasSub = user?.subscription?.active && user?.subscription?.expires_at && new Date(user.subscription.expires_at) > new Date();
  const hasPromo = company?.is_promoted && company?.promoted_until && new Date(company.promoted_until) > new Date();

  useEffect(() => {
    if (!isAuthenticated || !isCompany) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token, isAuthenticated, isCompany, navigate]);

  async function fetchData() {
    try {
      const [companyRes, responsesRes, offersRes, categoriesRes, messagesRes] = await Promise.all([
        fetch(`${API}/my-company`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }),
        fetch(`${API}/my-responses`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }),
        fetch(`${API}/offers?limit=10`),
        fetch(`${API}/categories`),
        fetch(`${API}/messages`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }),
      ]);
      if (companyRes.ok) {
        const companyData = await companyRes.json();
        setCompany(companyData);
        setReviews(companyData.reviews || []);
      }
      if (responsesRes.ok) { const data = await responsesRes.json(); setMyResponses(data.responses || []); }
      if (offersRes.ok) { const data = await offersRes.json(); setRecentOffers(data.offers || []); }
      if (categoriesRes.ok) setCategories(await categoriesRes.json());
      if (messagesRes.ok) setConversations(await messagesRes.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleReplyToReview(reviewId, replyText) {
    try {
      const res = await fetch(`${API}/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ reply: replyText }),
      });
      if (res.ok) {
        toast.success('Odpowiedź dodana!');
        fetchData();
        return true;
      } else {
        toast.error('Nie udało się dodać odpowiedzi');
        return false;
      }
    } catch (e) {
      toast.error('Błąd');
      return false;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const avgRating = company?.rating || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-dashboard">
      <div className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              {company?.logo_url ? (
                <img src={company.logo_url} alt="Logo" className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <div className="w-16 h-16 bg-slate-800 rounded-lg flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-slate-400" />
                </div>
              )}
              <div>
                <h1 className="font-heading font-bold text-2xl text-white">Panel firmy</h1>
                <p className="text-slate-400 mt-1">{company?.name || user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Elementy płatności - widoczne tylko w trybie płatnym */}
              {showPaymentUI && (
                <>
                  {hasSub ? (
                    <Badge className="bg-green-500 text-white px-4 py-2">Subskrypcja aktywna</Badge>
                  ) : (
                    <Link to="/subscription">
                      <Button className="bg-orange-500 hover:bg-orange-600">
                        <CreditCard className="w-4 h-4 mr-2" />Aktywuj subskrypcję
                      </Button>
                    </Link>
                  )}
                  {hasPromo ? (
                    <Badge className="bg-yellow-500 text-white px-4 py-2">
                      <Award className="w-4 h-4 mr-1" />Promowanie aktywne
                    </Badge>
                  ) : hasSub && (
                    <Link to="/subscription">
                      <Button variant="outline" className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/10">
                        <Award className="w-4 h-4 mr-2" />Promuj firmę
                      </Button>
                    </Link>
                  )}
                </>
              )}
              <Button onClick={() => navigate('/company/profile')} variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                <Building2 className="w-4 h-4 mr-2" />Profil firmy
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alert subskrypcji - widoczny tylko w trybie płatnym */}
        {showPaymentUI && !hasSub && (
          <Card className="mb-6 border-orange-500 border-2">
            <CardContent className="p-6 flex items-center gap-4">
              <AlertCircle className="w-10 h-10 text-orange-500 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-lg">Aktywuj subskrypcję</h3>
                <p className="text-slate-600">Aby odpowiadać na ogłoszenia i korzystać z czatu, aktywuj miesięczną subskrypcję za 15 zł netto + VAT.</p>
              </div>
              <Button onClick={() => navigate('/subscription')} className="bg-orange-500 hover:bg-orange-600">Aktywuj</Button>
            </CardContent>
          </Card>
        )}

        {/* Alert promowania - widoczny tylko w trybie płatnym */}
        {showPaymentUI && hasSub && !hasPromo && (
          <Card className="mb-6 border-yellow-400 border-2 bg-yellow-50">
            <CardContent className="p-6 flex items-center gap-4">
              <Award className="w-10 h-10 text-yellow-500 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-lg">Promuj swoją firmę!</h3>
                <p className="text-slate-600">Wyróżnij się na stronie głównej. Tylko 25 zł netto za 7 dni.</p>
              </div>
              <Button onClick={() => navigate('/subscription')} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                <Award className="w-4 h-4 mr-2" />Promuj
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{myResponses.length}</p>
                <p className="text-sm text-slate-500">Wysłanych ofert</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{avgRating > 0 ? avgRating.toFixed(1) : '0.0'}</p>
                <p className="text-sm text-slate-500">Ocena ({reviews.length})</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{recentOffers.length}</p>
                <p className="text-sm text-slate-500">Dostępnych ogłoszeń</p>
              </div>
            </CardContent>
          </Card>
          {/* Karta "Promowana" - widoczna tylko w wersji płatnej */}
          {showPaymentUI && (
            <Card className={hasPromo ? 'border-yellow-400 border-2' : ''}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${hasPromo ? 'bg-yellow-100' : 'bg-slate-100'}`}>
                  <Award className={`w-6 h-6 ${hasPromo ? 'text-yellow-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{hasPromo ? 'Tak' : 'Nie'}</p>
                  <p className="text-sm text-slate-500">Promowana</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="offers"><Search className="w-4 h-4 mr-2" />Ogłoszenia</TabsTrigger>
            <TabsTrigger value="responses"><MessageSquare className="w-4 h-4 mr-2" />Moje oferty ({myResponses.length})</TabsTrigger>
            <TabsTrigger value="reviews"><Star className="w-4 h-4 mr-2" />Opinie ({reviews.length})</TabsTrigger>
            <TabsTrigger value="messages"><MessageCircle className="w-4 h-4 mr-2" />Moje Wiadomości</TabsTrigger>
          </TabsList>

          <TabsContent value="offers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Najnowsze ogłoszenia</CardTitle>
                  <Button variant="outline" onClick={() => navigate('/offers')}>Zobacz wszystkie<ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentOffers.map((offer) => (
                    <Link key={offer.offer_id} to={`/offers/${offer.offer_id}`}>
                      <div className="border border-slate-200 rounded-lg p-4 hover:border-orange-500/50 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <Badge variant="secondary" className="text-xs">{categories.find(c => c.id === offer.category)?.name || offer.category}</Badge>
                          <span className="text-xs text-slate-500">{new Date(offer.created_at).toLocaleDateString('pl-PL')}</span>
                        </div>
                        <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">{offer.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{offer.description}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{offer.location}</span>
                          {offer.deadline && <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(offer.deadline).toLocaleDateString('pl-PL')}</span>}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="responses">
            <Card>
              <CardHeader><CardTitle className="font-heading">Wysłane oferty</CardTitle></CardHeader>
              <CardContent>
                {myResponses.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-slate-700 mb-2">Brak wysłanych ofert</h3>
                    <p className="text-slate-500 mb-6">Przeglądaj ogłoszenia i odpowiadaj na interesujące projekty</p>
                    <Button onClick={() => navigate('/offers')} className="bg-orange-500 hover:bg-orange-600">
                      <Search className="w-4 h-4 mr-2" />Przeglądaj ogłoszenia
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myResponses.map((response) => (
                      <div key={response.response_id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <Badge className={response.status === 'accepted' ? 'bg-green-100 text-green-700' : response.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                            {response.status === 'pending' ? 'Oczekuje' : response.status === 'accepted' ? 'Zaakceptowana' : 'Odrzucona'}
                          </Badge>
                          <span className="text-xs text-slate-500">{new Date(response.created_at).toLocaleDateString('pl-PL')}</span>
                        </div>
                        <Link to={`/offers/${response.offer_id}`}>
                          <p className="font-medium text-slate-900 hover:text-orange-600 mb-2">Oferta do ogłoszenia #{response.offer_id.slice(-8)}</p>
                        </Link>
                        <p className="text-sm text-slate-600 line-clamp-2">{response.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    Opinie klientów
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <StarDisplay rating={Math.round(avgRating)} />
                    <span className="font-bold">{avgRating > 0 ? avgRating.toFixed(1) : '-'}</span>
                    <span className="text-slate-500">({reviews.length} opinii)</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-slate-700 mb-2">Brak opinii</h3>
                    <p className="text-slate-500">Gdy klienci dodadzą opinie, pojawią się tutaj</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <ReviewCard key={review.review_id} review={review} onReply={handleReplyToReview} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Moje Wiadomości ({conversations.length})</CardTitle>
                  <Button onClick={() => navigate('/messages')} className="bg-orange-500 hover:bg-orange-600">
                    <MessageCircle className="w-4 h-4 mr-2" />Otwórz wiadomości
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {conversations.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-slate-700 mb-2">Brak wiadomości</h3>
                    <p className="text-slate-500 mb-6">Gdy klienci napiszą do Ciebie, zobaczysz je tutaj</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {conversations.slice(0, 5).map((conv) => (
                      <div 
                        key={conv.partner_id} 
                        className="border rounded-lg p-4 hover:border-orange-200 cursor-pointer transition-colors"
                        onClick={() => navigate(`/messages?user=${conv.partner_id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                              <MessageCircle className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium">{conv.partner_name || 'Użytkownik'}</p>
                              <p className="text-sm text-slate-500 truncate max-w-xs">{conv.last_content}</p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    ))}
                    {conversations.length > 5 && (
                      <Button onClick={() => navigate('/messages')} variant="outline" className="w-full">
                        Zobacz wszystkie ({conversations.length})
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
